/**
 * Simple IndexedDB-backed queue for mutations executed while offline.
 * Each entry stores { id, method, url, data, headers, ts, entity_type }.
 * On reconnect we replay them in FIFO order.
 */

const DB_NAME = "dkconnect-offline";
const DB_VERSION = 1;
const STORE = "mutations";

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("IndexedDB non disponible"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let result;
    Promise.resolve(fn(store)).then((r) => { result = r; }).catch(reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function enqueue(entry) {
  const payload = {
    method: entry.method,
    url: entry.url,
    data: entry.data,
    headers: entry.headers || {},
    entity_type: entry.entity_type || null,
    ts: Date.now(),
  };
  return tx("readwrite", (store) =>
    new Promise((res, rej) => {
      const req = store.add(payload);
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    })
  );
}

export async function listQueue() {
  return tx("readonly", (store) =>
    new Promise((res, rej) => {
      const req = store.getAll();
      req.onsuccess = () => res(req.result || []);
      req.onerror = () => rej(req.error);
    })
  );
}

export async function removeEntry(id) {
  return tx("readwrite", (store) =>
    new Promise((res, rej) => {
      const req = store.delete(id);
      req.onsuccess = () => res(true);
      req.onerror = () => rej(req.error);
    })
  );
}

export async function clearQueue() {
  return tx("readwrite", (store) =>
    new Promise((res, rej) => {
      const req = store.clear();
      req.onsuccess = () => res(true);
      req.onerror = () => rej(req.error);
    })
  );
}

export async function countQueue() {
  const items = await listQueue();
  return items.length;
}
